import { useEffect, useState } from 'react'

import { useArchiveFloor } from '../hooks/useArchiveFloor'
import { useEditFloor } from '../hooks/useEditFloor'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Button,
  Box as DarkBox,
  Flex,
  Modal,
  Spinner,
  TextInput,
  Tooltip,
} from '@hivewatch/dark'

import { FormFileUploadControl, StyledFormInputControl } from '@/components/ui'
import { getFacilityOption } from '@/utils/forms/optionBuilders'

import { ArchiveFloorModal } from '../components/ArchiveFloorModal'
import { EditFloorFormSkeleton } from '../components/EditFloorFormSkeleton'
import { FloorPlanCropper } from '../components/FloorPlanCropper'
import { FloorPlanRemove } from '../components/FloorPlanRemove'
import { PdfPageSelector } from '../components/PdfPageSelector'
import { FloorFormInputsIProps } from '../types/types'

interface DarkEditFloorModalIProps {
  floorId: string
  isOpen: boolean
  onClose: () => void
}

/**
 * Dark twin of EditFloorModal. The chrome comes from the dark Modal and the
 * two text fields from the dark TextInput; the upload, PDF page selector and
 * cropper are reused as-is because they are mode-keyed already and have no
 * dark equivalents.
 *
 * The name field is local state rather than react-hook-form: the dark
 * TextInput is controlled, and RHF is still needed for `file` only because
 * FormFileUploadControl is uncontrolled and takes a register ref.
 */
export const DarkEditFloorModal = ({
  floorId,
  isOpen,
  onClose,
}: DarkEditFloorModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const isArchiveFloorEnabled = useFeatureFlag(
    FeatureFlag.archiveFloorAndFacility
  )
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
  const [fileTooltipAnchor, setFileTooltipAnchor] =
    useState<HTMLElement | null>(null)
  const [name, setName] = useState('')
  const { unarchiveFloor, isUnarchiveLoading } = useArchiveFloor()

  const { watch, register, setValue } = useForm<FloorFormInputsIProps>({
    reValidateMode: 'onChange',
  })
  const fileUploadWatch = watch('file')

  const {
    floorDetails,
    isFloorDetailsLoading,
    isFloorPlanMarkedForRemoval,
    hasNewFile,
    isNewFileValid,
    canUploadFloorPlan,
    isFloorPlanSwapPending,
    isUpdateLoading,
    selectedPdfPage,
    isPageConfirmed,
    confirmPage,
    unconfirmPage,
    selectedFile,
    isPdf,
    markFloorPlanForRemoval,
    onUpdateFloor,
    setSelectedPdfPage,
    resetPdfPage,
    pdfSelectorRef,
    crop,
    setCrop,
    imagePreviewUrl,
    isPreviewLoading,
    shouldShowCropper,
    cropperRef,
    resetCrop,
    resetFloorPlanChange,
  } = useEditFloor({ floorId, onClose, fileUploadWatch })

  const floor = floorDetails?.floor

  useEffect(() => {
    if (floor) setName(floor.name ?? '')
  }, [isFloorDetailsLoading])

  const handleUnarchive = async () => {
    const isUnarchived = await unarchiveFloor(floorId)
    if (isUnarchived) handleClose()
  }

  const handleSave = () =>
    onUpdateFloor({
      facility: getFacilityOption(floor?.facility),
      file: fileUploadWatch,
      name,
    })

  // Legacy gated Save on react-hook-form's isDirty, which in practice meant
  // "the name changed or a file was attached". Same rule, stated directly.
  const hasNameChanged = name.trim() !== (floor?.name ?? '')
  const isSaveDisabled =
    isFloorDetailsLoading ||
    !name.trim() ||
    (!hasNameChanged && !hasNewFile && !isFloorPlanMarkedForRemoval) ||
    !isNewFileValid

  const clearPendingFloorPlanChange = () => {
    setValue('file', null)
    resetFloorPlanChange()
  }

  const handleClose = () => {
    clearPendingFloorPlanChange()
    onClose()
  }

  // The PDF selector and cropper are the only content that outgrows the body,
  // the same condition the legacy modal used to switch scrollBehavior.
  const isExpandedByFilePreview =
    (isPdf && Boolean(selectedFile) && !isPageConfirmed) ||
    (shouldShowCropper && Boolean(imagePreviewUrl))

  const archiveButton = floor?.archived ? (
    <Button
      colorScheme={colorScheme}
      data-testid='DarkEditFloorModal:unarchiveButton'
      disabled={isFloorDetailsLoading || isUpdateLoading}
      isLoading={isUnarchiveLoading}
      onClick={handleUnarchive}
      variant='secondary'
    >
      {t('floors:unarchive_button')}
    </Button>
  ) : (
    <Button
      colorScheme={colorScheme}
      data-testid='DarkEditFloorModal:archiveButton'
      disabled={isFloorDetailsLoading || isUpdateLoading}
      onClick={() => setIsArchiveModalOpen(true)}
      variant='danger'
    >
      {t('floors:archive_button')}
    </Button>
  )

  return (
    <>
      <Modal
        // Matches the width the legacy modal had on Chakra's `xl`; the default
        // dark size is a 1143px fixed-height canvas, far wider than two fields
        // and an upload need.
        bodyStyleOverrides={
          isExpandedByFilePreview
            ? { maxH: '60vh', overflowY: 'auto' }
            : undefined
        }
        colorScheme={colorScheme}
        footer={
          <DarkBox
            alignItems='center'
            display='flex'
            justifyContent='space-between'
            width='100%'
          >
            <DarkBox>{isArchiveFloorEnabled && archiveButton}</DarkBox>
            <DarkBox display='flex' gap='8px'>
              <Button
                colorScheme={colorScheme}
                onClick={handleClose}
                variant='tertiary'
              >
                {t('common:cancel')}
              </Button>
              <Button
                colorScheme={colorScheme}
                data-testid='DarkEditFloorModal:saveButton'
                disabled={isSaveDisabled}
                isLoading={isUpdateLoading}
                onClick={handleSave}
                variant='primary'
              >
                {t('common:save')}
              </Button>
            </DarkBox>
          </DarkBox>
        }
        isOpen={isOpen}
        isSaveHidden
        maxW='576px'
        onClose={handleClose}
        size='compact'
        title={t('floors:crud_edit_title')}
      >
        {isFloorDetailsLoading ? (
          <EditFloorFormSkeleton />
        ) : (
          <DarkBox
            data-testid='DarkEditFloorModal:body'
            display='flex'
            flexDirection='column'
            gap='16px'
          >
            <TextInput
              colorScheme={colorScheme}
              data-testid='DarkEditFloorModal:floorName'
              hasWarning={!name.trim()}
              label={t('floors:crud_floor_name_label')}
              onValueChange={setName}
              placeholder={t('floors:crud_floor_name_placeholder')}
              value={name}
              warningText={t('floors:crud_floor_name_required')}
              width='100%'
            />
            <TextInput
              colorScheme={colorScheme}
              data-testid='DarkEditFloorModal:facility'
              disabled
              label={t('floors:crud_facility_label')}
              placeholder={t('floors:crud_facility_placeholder')}
              value={floor?.facility?.name ?? ''}
              width='100%'
            />
            <StyledFormInputControl>
              {floor?.floorPlan && (
                <FloorPlanRemove
                  colorScheme={colorScheme}
                  floorPlanName={floor.floorPlan.name}
                  isMarkedForRemoval={isFloorPlanMarkedForRemoval}
                  isUpdateLoading={isUpdateLoading}
                  onRemove={markFloorPlanForRemoval}
                  onUndo={clearPendingFloorPlanChange}
                />
              )}
              {canUploadFloorPlan && (
                <>
                  <DarkBox
                    onMouseEnter={(event) =>
                      setFileTooltipAnchor(event.currentTarget)
                    }
                    onMouseLeave={() => setFileTooltipAnchor(null)}
                  >
                    <FormFileUploadControl
                      accept='.png,.pdf,.jpg,.jpeg'
                      colorScheme={colorScheme}
                      dataTestId='DarkEditFloorModal:floorplan'
                      errorMessage={
                        hasNewFile &&
                        !isNewFileValid &&
                        t('floors:crud_file_error')
                      }
                      id='file'
                      inputRef={register({ required: false })}
                      isDisabled={isUpdateLoading}
                      isInvalid={!isNewFileValid}
                      label={t('floors:crud_floor_plan_label')}
                      onFileRemoved={() => {
                        setValue('file', null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                        resetPdfPage()
                        resetCrop()
                      }}
                      placeholder={t('floors:crud_floor_plan_placeholder')}
                    />
                  </DarkBox>
                  <Tooltip
                    anchorEl={fileTooltipAnchor}
                    colorScheme={colorScheme}
                    label={t('floors:crud_file_type_tooltip')}
                    variant='description'
                  />
                  {isFloorPlanSwapPending && (
                    <DarkBox
                      data-testid='EditFloorModal:floorPlanSwapAlert'
                      mt='12px'
                    >
                      <Alert
                        colorScheme={colorScheme}
                        description={t('floors:crud_swap_save_required')}
                        variant='info'
                      />
                    </DarkBox>
                  )}
                </>
              )}
            </StyledFormInputControl>
            {isPdf &&
              selectedFile &&
              canUploadFloorPlan &&
              !isPageConfirmed && (
                <DarkBox ref={pdfSelectorRef}>
                  <PdfPageSelector
                    isDisabled={isUpdateLoading}
                    onConfirm={confirmPage}
                    onPageChange={setSelectedPdfPage}
                    pdfFile={selectedFile}
                    selectedPage={selectedPdfPage}
                  />
                </DarkBox>
              )}
            {isPreviewLoading && (
              <Flex alignItems='center' justifyContent='center' py='32px'>
                <Spinner size='lg' />
              </Flex>
            )}
            {shouldShowCropper && imagePreviewUrl && canUploadFloorPlan && (
              <DarkBox ref={cropperRef}>
                <FloorPlanCropper
                  crop={crop}
                  disabled={isUpdateLoading}
                  imageUrl={imagePreviewUrl}
                  onChangePage={isPdf ? unconfirmPage : undefined}
                  onCropChange={(_, percentCrop) => setCrop(percentCrop)}
                  onReset={resetCrop}
                />
              </DarkBox>
            )}
          </DarkBox>
        )}
      </Modal>
      {isArchiveFloorEnabled && (
        <ArchiveFloorModal
          colorScheme={colorScheme}
          facilityId={floor?.facility?.id}
          floorId={floorId}
          floorName={floor?.name}
          isOpen={isArchiveModalOpen}
          onArchived={handleClose}
          onClose={() => setIsArchiveModalOpen(false)}
        />
      )}
    </>
  )
}
